/* eslint-disable no-magic-numbers, unicorn/no-null */

import type { FC } from 'react';
import { Badge, Text, Tooltip } from '@mantine/core';
import { Power, Zap } from 'lucide-react';
import type { TCustomerLink, TProjectVersion } from '@newo/api/gateway';
import { formatVersionLabel } from '@/pages/Project/components/Files/VersionsSidebar/VersionsSidebar.utils';

interface ActiveVersionBadgeProps {
    link: TCustomerLink | null | undefined;
    version: TProjectVersion | null | undefined;
}

export const ActiveVersionBadge: FC<ActiveVersionBadgeProps> = ({
    link,
    version
}) => {
    if (!link || (!link.project_version_id && !link.registry_idn)) {
        return (
            <Tooltip
                label="No active customer link — runtime has no source. Activate a published version or a registry module."
                position="bottom"
            >
                <Badge
                    color="gray"
                    leftSection={<Power size={12} />}
                    radius="sm"
                    size="sm"
                    tt="none"
                    variant="light"
                >
                    Not active
                </Badge>
            </Tooltip>
        );
    }

    if (link.project_version_id) {
        const label = version
            ? formatVersionLabel(version)
            : `${link.project_version_id.slice(0, 8)}…`;
        return (
            <Tooltip
                label="Active — currently handling runtime events (local version)"
                position="bottom"
            >
                <Badge
                    color="dark"
                    leftSection={<Zap size={12} />}
                    radius="sm"
                    size="sm"
                    tt="none"
                    variant="filled"
                >
                    Active:&nbsp;
                    <Text inherit component="span" ff="monospace">
                        {label}
                    </Text>
                </Badge>
            </Tooltip>
        );
    }

    if (link.registry_idn && link.module_idn) {
        const versionSuffix = link.module_version
            ? `@${link.module_version}`
            : ' (latest)';
        return (
            <Tooltip
                label="Active — currently handling runtime events (registry module)"
                position="bottom"
            >
                <Badge
                    color="blue"
                    leftSection={<Zap size={12} />}
                    radius="sm"
                    size="sm"
                    tt="none"
                    variant="filled"
                >
                    Active:&nbsp;
                    <Text inherit component="span" ff="monospace">
                        {link.registry_idn}/{link.module_idn}
                        {versionSuffix}
                    </Text>
                </Badge>
            </Tooltip>
        );
    }

    return null;
};
